import type { ThunkAction } from '@reduxjs/toolkit';
import { usersActions, usersSelectors } from 'store/slices/users/config';
import type {
  CancelOrderPayload,
  ConfirmOrder,
  Order,
  OrderRequestBody,
} from 'types/orders';
import { OrderStatus } from 'types/orders';
import type { RootState } from 'store/index';
import type { DropOffConfirmationPayload } from 'store/services/order.service';
import { OrderService } from 'store/services/order.service';
import { generalSliceActions } from 'store/slices/general/config';
import {
  rentDetailsActions,
  rentDetailsSelectors,
} from 'store/slices/rentDetails/config';
import { RentalDrawerSteps } from 'components/Reservation/types/types';
import { CommentsService } from 'store/services/comments.service';
import { carsActions, carsSelectors } from 'store/slices/cars/config';
import moment from 'moment';
import { getDaysCount } from 'components/RentalDrawer/helpers/dateHelpers';
import type { StripeCardNumberElement } from '@stripe/stripe-js';
import { stripeServiceInstance } from 'store/services/stripe.service';
import { ordersActions, ordersSelectors } from './config';
import type { SecondaryDriver } from './orders.slice';

export interface PaginationParams {
  page: number;
}
export const getAllOrders =
  (
    params?: Record<keyof Order, string>,
  ): ThunkAction<void, RootState, unknown, any> =>
  async (dispatch: any) => {
    try {
      dispatch(ordersActions.setOrdersPendingStatus(true));
      const response = await OrderService.getAllOrders(params);
      dispatch(ordersActions.setAllOrders(response.rows));
      dispatch(ordersActions.setOrdersTotalCount(response.count));
    } catch (err: any) {
      dispatch(
        generalSliceActions.setAppMessage({
          type: 'error',
          message: err.message,
        }),
      );
    } finally {
      dispatch(ordersActions.setOrdersPendingStatus(false));
    }
  };

export const getAllPendingOrders =
  (params: PaginationParams): ThunkAction<void, RootState, unknown, any> =>
  async (dispatch) => {
    try {
      dispatch(ordersActions.setIsPendingOrdersLoading(true));
      const response = await OrderService.getPendingOrders(params);
      dispatch(ordersActions.setPendingOrdersCount(response.count));
      dispatch(ordersActions.setPendingOrders(response.rows));
    } catch (err: any) {
      console.error(err);
    } finally {
      dispatch(ordersActions.setIsPendingOrdersLoading(false));
    }
  };

export const createNewPendingOrder =
  (
    order: Omit<OrderRequestBody, 'paymentMethodId'>,
    cardElement: StripeCardNumberElement,
  ): ThunkAction<void, RootState, unknown, any> =>
  async (dispatch, getState) => {
    try {
      const stripeInstance = stripeServiceInstance.getStripeInstance();
      if (!stripeInstance) {
        return;
      }
      dispatch(rentDetailsActions.setIsOrderCreationInProcess(true));
      dispatch(ordersActions.setOrdersPendingStatus(true));
      const step = rentDetailsSelectors.selectRentalDrawerStep(getState());
      const currentLinkedList = RentalDrawerSteps[step];
      const selectedCar = carsSelectors.selectSelectedCar(getState());
      const profile = usersSelectors.selectProfile(getState());
      const token = localStorage.getItem('token');
      if (!token) {
        return;
      }
      if (!profile?.id) {
        return;
      }
      const { error, paymentMethod } = await stripeInstance.createPaymentMethod(
        {
          type: 'card',
          card: cardElement,
        },
      );

      if (error?.message) {
        dispatch(
          generalSliceActions.setAppMessage({
            type: 'error',
            message: error.message,
          }),
        );
        return;
      }

      if (paymentMethod?.id) {
        const newPendingOrder = await OrderService.createNewPendingOrder({
          ...order,
          paymentMethodId: paymentMethod?.id,
        });
        dispatch(ordersActions.setSelectedOrder(newPendingOrder));
        dispatch(ordersActions.setNewOrder(newPendingOrder));
        dispatch(
          carsActions.updateCar({
            ...selectedCar,
            orders: (selectedCar.orders || []).concat(newPendingOrder),
          }),
        );

        dispatch(
          usersActions.setProfile({
            ...profile,
            orders: (profile.orders || []).concat(newPendingOrder),
            subscriptions: order.subscriptionId
              ? (profile.subscriptions || []).map((item) =>
                  item.id === order.subscriptionId
                    ? {
                        ...item,
                        daysUsed:
                          item.daysUsed +
                          getDaysCount(
                            moment(order.rentStartDate),
                            moment(order.rentEndDate),
                          ),
                      }
                    : item,
                )
              : profile.subscriptions,
          }),
        );
        if (currentLinkedList.next) {
          dispatch(rentDetailsActions.setDrawerStep(currentLinkedList.next));
        }
      }
    } catch (err: any) {
      dispatch(
        generalSliceActions.setAppMessage({
          type: 'error',
          message: err.message,
        }),
      );
    } finally {
      dispatch(ordersActions.setOrdersPendingStatus(false));
      dispatch(rentDetailsActions.setIsOrderCreationInProcess(false));
    }
  };

export const confirmOrder =
  (
    body: ConfirmOrder,
    files: Array<File>,
  ): ThunkAction<void, RootState, unknown, any> =>
  async (dispatch) => {
    try {
      if (!body.id) {
        return;
      }
      dispatch(ordersActions.setOrdersPendingStatus(true));
      const response = await OrderService.confirmOrder(body, files);
      dispatch(ordersActions.updateOrder(response));
    } catch (err: any) {
      throw new Error(err);
    } finally {
      dispatch(ordersActions.setOrdersPendingStatus(false));
    }
  };

export const getOrderById =
  (id: string): ThunkAction<void, RootState, unknown, any> =>
  async (dispatch) => {
    try {
      dispatch(ordersActions.setOrdersPendingStatus(true));
      const response = await OrderService.getOrderById(id);
      dispatch(ordersActions.setSelectedOrder(response));
    } catch (err: any) {
      throw new Error(err);
    } finally {
      dispatch(ordersActions.setOrdersPendingStatus(false));
    }
  };

export const deleteOrder =
  (id: string): ThunkAction<void, RootState, unknown, any> =>
  async (dispatch) => {
    try {
      dispatch(ordersActions.setOrdersPendingStatus(true));
      await OrderService.deleteOrder(id);
      dispatch(ordersActions.removeOrder(id));
      dispatch(ordersActions.setSelectedOrder(null));
    } catch (e: any) {
      dispatch(
        generalSliceActions.setAppMessage({
          type: 'error',
          message: 'Something went wrong',
        }),
      );
    } finally {
      dispatch(ordersActions.setOrdersPendingStatus(false));
    }
  };

export const uploadOrderFiles =
  (file: File): ThunkAction<void, RootState, unknown, any> =>
  async (dispatch, getState) => {
    try {
      const order = ordersSelectors.selectSelectedOrder(getState());
      if (!order?.id) {
        return;
      }
      const response = await OrderService.updateOrderFiles(file, order.id);
      dispatch(ordersActions.addNewOrderFile(response));
    } catch (err) {
      dispatch(
        generalSliceActions.setAppMessage({
          type: 'error',
          message: 'Something went wrong on changing order files',
        }),
      );
    }
  };

export const removeOrderFile =
  (id: string): ThunkAction<void, RootState, unknown, any> =>
  async (dispatch) => {
    try {
      await OrderService.removeOrderFile(id);
      dispatch(ordersActions.removeSelectedOrderFile(id));
    } catch (err: any) {
      dispatch(
        generalSliceActions.setAppMessage({
          type: 'error',
          message: 'Something went wrong on removing order files',
        }),
      );
    }
  };

export const editOrder =
  (payload: Partial<Order>): ThunkAction<void, RootState, unknown, any> =>
  async (dispatch) => {
    try {
      dispatch(ordersActions.setOrdersPendingStatus(true));
      const response = await OrderService.editOrder(payload);
      dispatch(ordersActions.setSelectedOrder(response));
      dispatch(ordersActions.updateOrder(response));
    } catch (err: any) {
      dispatch(
        generalSliceActions.setAppMessage({
          type: 'error',
          message: 'Something went wrong when editing order',
        }),
      );
    } finally {
      dispatch(ordersActions.setOrdersPendingStatus(false));
    }
  };

export const confirmDropOff =
  (
    payload: Omit<DropOffConfirmationPayload, 'id'>,
  ): ThunkAction<void, RootState, unknown, any> =>
  async (dispatch, getState) => {
    try {
      const selectedOrder = ordersSelectors.selectSelectedOrder(getState());
      if (!selectedOrder?.id) {
        return;
      }
      dispatch(ordersActions.setOrdersPendingStatus(true));
      const response = await OrderService.confirmDropOff({
        ...payload,
        id: selectedOrder.id,
      });
      dispatch(ordersActions.updateOrder(response));
      dispatch(ordersActions.setSelectedOrder(null));
    } catch (err: any) {
      dispatch(
        generalSliceActions.setAppMessage({
          type: 'error',
          message: err.message,
        }),
      );
    } finally {
      dispatch(ordersActions.setOrdersPendingStatus(false));
    }
  };

export const createComment =
  (content: string): ThunkAction<void, RootState, unknown, any> =>
  async (dispatch, getState) => {
    try {
      const selectedOrder = ordersSelectors.selectSelectedOrder(getState());
      if (!selectedOrder?.id) {
        return;
      }

      const response = await CommentsService.createComment({
        content,
        orderId: selectedOrder.id,
      });
      const newOrder = {
        ...selectedOrder,
        comments: selectedOrder.comments.concat(response),
      };
      dispatch(ordersActions.updateOrder(newOrder));
      dispatch(ordersActions.setSelectedOrder(newOrder));
    } catch (err: any) {
      throw new Error(err);
    }
  };

export const updateComment =
  (
    newContent: string,
    contentId: string,
  ): ThunkAction<void, RootState, unknown, any> =>
  async (dispatch, getState) => {
    try {
      const selectedOrder = ordersSelectors.selectSelectedOrder(getState());
      if (!selectedOrder?.id) {
        return;
      }
      if (!contentId) {
        return;
      }
      dispatch(ordersActions.setPendingCommentId(contentId));
      const response = await CommentsService.updateComment(
        newContent,
        contentId,
      );
      const newOrder = {
        ...selectedOrder,
        comments: selectedOrder.comments.map((item) =>
          item.id === response.id ? response : item,
        ),
      };
      dispatch(ordersActions.updateOrder(newOrder));
      dispatch(ordersActions.setSelectedOrder(newOrder));
    } catch (err: any) {
      throw new Error(err);
    } finally {
      dispatch(ordersActions.setPendingCommentId(null));
    }
  };

export const removeComment =
  (commentId: string): ThunkAction<void, RootState, unknown, any> =>
  async (dispatch, getState) => {
    try {
      const selectedOrder = ordersSelectors.selectSelectedOrder(getState());
      if (!selectedOrder?.id) {
        return;
      }
      if (!commentId) {
        return;
      }
      dispatch(ordersActions.setPendingCommentId(commentId));
      await CommentsService.removeComment(commentId);
      const newOrder = {
        ...selectedOrder,
        comments: selectedOrder.comments.filter(
          (item) => item.id !== commentId,
        ),
      };
      dispatch(ordersActions.updateOrder(newOrder));
      dispatch(ordersActions.setSelectedOrder(newOrder));
    } catch (err: any) {
      throw new Error(err);
    } finally {
      dispatch(ordersActions.setPendingCommentId(null));
    }
  };

export const attachSecondaryDriver =
  (
    payload: SecondaryDriver | { secondaryDriverId: string },
    cb: () => void,
  ): ThunkAction<void, RootState, unknown, any> =>
  async (dispatch, getState) => {
    try {
      const selectedOrder = ordersSelectors.selectSelectedOrder(getState());
      if (!selectedOrder?.id) {
        return;
      }
      const response = await OrderService.attachSecondaryDriver(
        payload,
        selectedOrder.id,
      );

      dispatch(ordersActions.updateOrder(response));
      dispatch(ordersActions.setSelectedOrder(response));
      if (cb) {
        cb();
      }
    } catch (err: any) {
      throw new Error(err);
    }
  };

export const updateSecurityDepositDocument =
  (file: File, cb?: () => void): ThunkAction<void, RootState, unknown, any> =>
  async (dispatch, getState) => {
    try {
      const selectedOrder = ordersSelectors.selectSelectedOrder(getState());
      if (!selectedOrder?.id) {
        return;
      }
      const response = await OrderService.updateSecurityDepositDocument(
        file,
        selectedOrder.id,
      );
      if (cb) {
        cb();
      }
      dispatch(ordersActions.updateOrder(response));
      dispatch(ordersActions.setSelectedOrder(response));
    } catch (err: any) {
      dispatch(
        generalSliceActions.setAppMessage({
          type: 'error',
          message:
            'Something went wrong in updateSecurityDepositDocument function',
        }),
      );
    }
  };

export const releaseSecurityDeposit =
  (cb?: () => void): ThunkAction<void, RootState, unknown, any> =>
  async (dispatch, getState) => {
    try {
      const selectedOrder = ordersSelectors.selectSelectedOrder(getState());
      if (!selectedOrder?.id) {
        return;
      }
      const response = await OrderService.releaseSecurityDeposit(
        selectedOrder.id,
      );
      dispatch(ordersActions.updateOrder(response));
      dispatch(ordersActions.setSelectedOrder(response));
      if (cb) {
        cb();
      }
    } catch (err: any) {
      throw new Error(err);
    }
  };

export const updateMultipleOrders =
  (
    ids: Array<string>,
    body: Partial<Order>,
  ): ThunkAction<void, RootState, unknown, any> =>
  async (dispatch) => {
    try {
      if (!ids || !Array.isArray(ids)) {
        return;
      }
      dispatch(ordersActions.setOrdersPendingStatus(true));
      const response = await OrderService.updateMultipleOrders(ids, body);
      const parsedOrders = response.reduce(
        (prev, curr) => ({
          ...prev,
          [curr.id]: curr,
        }),
        {} as Record<string, Order>,
      );
      dispatch(ordersActions.editMultipleOrders(parsedOrders));
    } catch (err: any) {
      throw new Error(err);
    } finally {
      dispatch(ordersActions.setOrdersPendingStatus(false));
    }
  };

export const switchDeposit =
  (cb?: () => void): ThunkAction<void, RootState, unknown, any> =>
  async (dispatch, getState) => {
    try {
      const selectedOrder = ordersSelectors.selectSelectedOrder(getState());
      if (!selectedOrder?.id) {
        return;
      }
      const order = await OrderService.switchDeposit(selectedOrder.id);
      dispatch(ordersActions.updateOrder(order));
      dispatch(ordersActions.setSelectedOrder(order));
      if (cb) {
        cb();
      }
    } catch (err: any) {
      throw new Error(err);
    }
  };

export const approveOrder =
  (id: string): ThunkAction<void, RootState, unknown, any> =>
  async (dispatch) => {
    try {
      dispatch(ordersActions.setOrdersPendingStatus(true));
      const order = await OrderService.approveOrder(id);
      dispatch(ordersActions.setSelectedOrder(order));
      dispatch(ordersActions.updateOrder(order));
    } catch (err: any) {
      dispatch(
        generalSliceActions.setAppMessage({
          type: 'error',
          message: err.message,
        }),
      );
    } finally {
      dispatch(ordersActions.setOrdersPendingStatus(false));
    }
  };

export const refuseOrder =
  (id: string, note: string): ThunkAction<void, RootState, unknown, any> =>
  async (dispatch) => {
    try {
      dispatch(ordersActions.setOrdersPendingStatus(true));
      const order = await OrderService.refuseOrder(id, note);
      dispatch(ordersActions.setSelectedOrder(order));
      dispatch(ordersActions.updateOrder(order));
    } catch (err: any) {
      dispatch(
        generalSliceActions.setAppMessage({
          type: 'error',
          message: err.message,
        }),
      );
    } finally {
      dispatch(ordersActions.setOrdersPendingStatus(false));
    }
  };

export const cancelOrder =
  (payload: CancelOrderPayload): ThunkAction<void, RootState, unknown, any> =>
  async (dispatch, getState) => {
    try {
      dispatch(ordersActions.setOrdersPendingStatus(true));
      const order = ordersSelectors.selectSelectedOrder(getState());
      if (!order) {
        return;
      }

      const response = await OrderService.editOrder({
        id: order.id,
        notesAtCancel: payload.note,
        cancelReason: payload.reason,
        status: OrderStatus.REFUSED,
      });
      dispatch(ordersActions.setSelectedOrder(response));
      dispatch(ordersActions.updateOrder(response));
      dispatch(ordersActions.setPickupReturnsTab(null));
      dispatch(ordersActions.setSelectedOrder(null));
    } catch (err: any) {
      dispatch(
        generalSliceActions.setAppMessage({
          type: 'error',
          message: err.message,
        }),
      );
    } finally {
      dispatch(ordersActions.setOrdersPendingStatus(false));
    }
  };

export const confirmPartiallyOrder =
  (
    id: string,
    cardElement: StripeCardNumberElement,
    cb: () => void,
  ): ThunkAction<void, RootState, unknown, any> =>
  async (dispatch) => {
    try {
      const stripeInstance = stripeServiceInstance.getStripeInstance();
      if (!stripeInstance) {
        return;
      }
      dispatch(generalSliceActions.setPaymentButtonIsPending(true));
      const { error, paymentMethod } = await stripeInstance.createPaymentMethod(
        {
          type: 'card',
          card: cardElement,
        },
      );

      if (error?.message) {
        dispatch(
          generalSliceActions.setAppMessage({
            type: 'error',
            message: error.message,
          }),
        );
        return;
      }

      if (paymentMethod?.id) {
        await OrderService.confirmPartiallyOrder(id, paymentMethod.id);
        if (cb) {
          cb();
        }
      }
    } catch (err: any) {
      dispatch(
        generalSliceActions.setAppMessage({
          type: 'error',
          message: err.message,
        }),
      );
    } finally {
      dispatch(generalSliceActions.setPaymentButtonIsPending(false));
    }
  };

export const refund =
  (amount: number): ThunkAction<void, RootState, unknown, any> =>
  async (dispatch, getState) => {
    try {
      const order = ordersSelectors.selectSelectedOrder(getState());
      if (!order) {
        return;
      }
      dispatch(ordersActions.setRefundPendingStatus(true));
      const response = await OrderService.refund(order.id, amount);
      dispatch(ordersActions.setSelectedOrder(response));
      dispatch(ordersActions.updateOrder(response));
    } catch (err: any) {
      dispatch(
        generalSliceActions.setAppMessage({
          type: 'error',
          message: err.message,
        }),
      );
    } finally {
      dispatch(ordersActions.setRefundPendingStatus(false));
    }
  };
